import {
  absenceTypeEnum,
  HOUR_SHEET_REVIEW_REASONS,
  hourSheets,
} from '@markku/db'
import { createInsertSchema, createSelectSchema } from 'drizzle-zod'
import { z } from 'zod'

// Flags the approver should manually verify, persisted on the sheet (text[]) so
// they survive submit. These mean the automatic km/allowance calc was unreliable
// and kilometers need a manual check. Long days are handled by the variance
// mechanism (varianceReasonCategory), not here.
export const hourSheetReviewReasonSchema = z.enum(HOUR_SHEET_REVIEW_REASONS)

export const selectHourSheetSchema = createSelectSchema(hourSheets, {
  reviewReasons: z.array(hourSheetReviewReasonSchema),
})

const hourSheetUserSchema = z
  .object({
    id: z.number(),
    firstName: z.string(),
    lastName: z.string(),
    email: z.string(),
  })
  .nullable()

export const selectHourSheetWithUserSchema = selectHourSheetSchema.extend({
  user: hourSheetUserSchema,
  approvedByUser: hourSheetUserSchema.optional(),
})

// Zero-padded 'HH:MM'. Both time inputs already emit exactly this (Mantine's
// TimePicker without `withSeconds`, and TimeInput's native input[type=time]),
// but hour_sheets_no_overlapping_hours slices the string positionally, and
// 'HH:MM:SS' would parse as HH:00 — dropping the minutes with no error. Pin the
// format here, the only write path, rather than let that reach the constraint.
const timeOfDaySchema = z
  .string()
  .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Kellonajan muoto on HH:MM')

// Clearing "Lopetusaika" in the ERP edit modal leaves the TimeInput empty, and
// its native input[type=time] reports that as ''. A day with no end time is a
// real state — the worker hasn't closed it yet — so read the empty field as
// null instead of failing the format check on it.
const clearableTimeOfDaySchema = z.preprocess(
  (value) => (value === '' ? null : value),
  timeOfDaySchema.nullable(),
)

// userId defaults to the caller; officers/admins may set it to log a day on
// behalf of a worker. Approval fields are gated by the router transform (only
// officers/admins may set them).
export const upsertHourSheetSchema = createInsertSchema(hourSheets, {
  reviewReasons: z.array(hourSheetReviewReasonSchema).optional(),
  startedTime: timeOfDaySchema,
  endedTime: clearableTimeOfDaySchema.optional(),
})
  .omit({
    id: true,
    createdAt: true,
    updatedAt: true,
    deletedAt: true,
  })
  .partial({ userId: true })

export const hourSheetDraftVisitSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  endTime: z.string(),
  title: z.string().nullable(),
  location: z.string().nullable(),
})

// A single day's visit as shown to the approver reviewing an hour sheet. Mirrors
// the inputs the allowance calc uses, so they can see why the auto allowance may
// be off: `hasLocation` is false when neither coordinates nor an address could be
// resolved, and `perDiemEligibleCity` is false for home-area cities (which never
// earn per diem regardless of distance).
export const hourSheetDayVisitSchema = z.object({
  id: z.number(),
  startTime: z.string(),
  endTime: z.string(),
  title: z.string().nullable(),
  address: z.string().nullable(),
  city: z.string().nullable(),
  hasLocation: z.boolean(),
  perDiemEligibleCity: z.boolean(),
})

export const hourSheetDraftWarningSchema = z.enum([
  'NO_TEAM',
  'NO_OFFICE_CONFIGURED',
  'VISIT_MISSING_COORDS',
  'DISTANCE_LOOKUP_FAILED',
])

export const hourSheetDraftSchema = z.object({
  date: z.string(),
  teamId: z.number().nullable(),
  timesheetMode: z.enum(['visit_based', 'office_based']).nullable(),
  startedTime: z.string().nullable(),
  endedTime: z.string().nullable(),
  lunchMinutes: z.number(),
  driveFromOfficeMin: z.number().nullable(),
  driveToOfficeMin: z.number().nullable(),
  farthestVisitKm: z.number().nullable(),
  allowance: z.enum(['none', 'meal', 'half_per_diem', 'per_diem']),
  visits: z.array(hourSheetDraftVisitSchema),
  workDurationMin: z.number(),
  shouldPromptVarianceReason: z.boolean(),
  existingHourSheetId: z.number().nullable(),
  warnings: z.array(hourSheetDraftWarningSchema),
  reviewReasons: z.array(hourSheetReviewReasonSchema),
})

export const daySheetSubmittedSchema = z.object({
  date: z.string(),
  status: z.literal('submitted'),
  hourSheet: selectHourSheetSchema,
})

export const daySheetMissingSchema = z.object({
  date: z.string(),
  status: z.literal('missing'),
  visitCount: z.number(),
  firstVisitStart: z.string().nullable(),
  lastVisitEnd: z.string().nullable(),
  defaultStart: z.string().nullable(),
  defaultEnd: z.string().nullable(),
})

export const daySheetAbsenceSchema = z.object({
  date: z.string(),
  status: z.literal('absence'),
  absenceId: z.number(),
  absenceType: z.enum([
    'sick',
    'child_sick',
    'vacation',
    'paid_leave',
    'unpaid_leave',
    'parental_leave',
    'other',
  ]),
  absenceStatus: z.enum(['pending', 'approved', 'rejected']),
})

export const daySheetEntrySchema = z.discriminatedUnion('status', [
  daySheetSubmittedSchema,
  daySheetMissingSchema,
  daySheetAbsenceSchema,
])

// Working-time balance for one 8-week balancing period (tasoittumisjakso).
// Minutes throughout; `payout` is how the surplus splits into TES pay tiers.
export const hourSheetBalanceSchema = z.object({
  period: z.object({ start: z.string(), end: z.string() }),
  targetDays: z.number(),
  targetMinutes: z.number(),
  // Worked time counted towards the balance (the >10 h/day share is excluded).
  workedMinutes: z.number(),
  // Already paid with the month's salary, so kept out of the balance.
  over10hMinutes: z.number(),
  // Working days credited at 7.5 h: any absence day and days before the
  // employment start, so neither drags the balance down.
  creditedDays: z.number(),
  creditedMinutes: z.number(),
  balanceMinutes: z.number(),
  payout: z.object({
    totalMinutes: z.number(),
    extra50Minutes: z.number(),
    overtime50Minutes: z.number(),
    overtime100Minutes: z.number(),
  }),
})

export const hourSheetBalanceRowSchema = hourSheetBalanceSchema.extend({
  userId: z.number(),
  employeeId: z.number(),
  firstName: z.string(),
  lastName: z.string(),
})

// Payroll summary: one row per employee for a date range, aggregating everything
// the salary clerk needs (total hours, absences, tax-free allowance counts) so
// they don't have to gather it from the per-day approval list by hand.
export const hourSheetSummaryAllowanceSchema = z.object({
  count: z.number(),
  // The dates (YYYY-MM-DD) the allowance was earned, ascending — listed so the
  // clerk can cross-check individual days.
  dates: z.array(z.string()),
})

export const hourSheetSummaryAbsenceSchema = z.object({
  type: z.enum(absenceTypeEnum.enumValues),
  // Clipped to the queried range, so per-month dates and the day count reflect
  // only the days that fall inside the period being exported.
  startDate: z.string(),
  endDate: z.string(),
  days: z.number(),
  pending: z.boolean(),
})

export const hourSheetSummaryRowSchema = z.object({
  userId: z.number(),
  // The employee record id (for linking to /employees/:id). Null if the user has
  // no employee row — e.g. an officer who logged hours without being an employee.
  employeeId: z.number().nullable(),
  firstName: z.string(),
  lastName: z.string(),
  totalMinutes: z.number(),
  workDays: z.number(),
  // Work days in the period whose worked time (excl. lunch) exceeded 10 h, and
  // the total minutes those days ran over the 10 h mark. Surfaced for the salary
  // clerk to spot long days (e.g. "12 pv / 18,5 h").
  overtimeDays: z.number(),
  overtimeMinutes: z.number(),
  // Own-car kilometers summed across the period (verovapaa kilometrikorvaus).
  personalCarKm: z.number(),
  // Provisiot maksuun: confirmed commission (euros) for visits whose actual
  // completion falls in the period. Anchored on completedAt, so it can differ
  // from the startTime-anchored Käyntitilastot Provisiot box — by design.
  commissionPayable: z.number(),
  absences: z.array(hourSheetSummaryAbsenceSchema),
  // Approved days only — see buildRow.
  absenceDays: z.number(),
  meal: hourSheetSummaryAllowanceSchema,
  halfPerDiem: hourSheetSummaryAllowanceSchema,
  perDiem: hourSheetSummaryAllowanceSchema,
  // Balancing periods that closed inside the range, with the surplus to pay
  // out in this payroll run. Usually zero or one.
  closedPeriods: z.array(hourSheetBalanceSchema),
})

export const hourSheetSummarySchema = z.array(hourSheetSummaryRowSchema)

export type HourSheet = z.infer<typeof selectHourSheetSchema>
export type HourSheetWithUser = z.infer<typeof selectHourSheetWithUserSchema>
export type UpsertHourSheet = z.infer<typeof upsertHourSheetSchema>
export type HourSheetDraft = z.infer<typeof hourSheetDraftSchema>
export type HourSheetDraftVisit = z.infer<typeof hourSheetDraftVisitSchema>
export type HourSheetDayVisit = z.infer<typeof hourSheetDayVisitSchema>
export type HourSheetDraftWarning = z.infer<typeof hourSheetDraftWarningSchema>
export type HourSheetReviewReason = z.infer<typeof hourSheetReviewReasonSchema>
export type DaySheetEntry = z.infer<typeof daySheetEntrySchema>
export type DaySheetSubmitted = z.infer<typeof daySheetSubmittedSchema>
export type DaySheetMissing = z.infer<typeof daySheetMissingSchema>
export type DaySheetAbsence = z.infer<typeof daySheetAbsenceSchema>
export type HourSheetSummaryRow = z.infer<typeof hourSheetSummaryRowSchema>
export type HourSheetBalance = z.infer<typeof hourSheetBalanceSchema>
export type HourSheetBalanceRow = z.infer<typeof hourSheetBalanceRowSchema>
export type HourSheetSummaryAbsence = z.infer<
  typeof hourSheetSummaryAbsenceSchema
>
